'use client';

import Image from 'next/image';
import Link from '@/components/LocaleLink';
import { MapPin, Clock, Star, Footprints } from 'lucide-react';
import { Tour } from '@/types';
import { useTranslation } from '@/i18n/LanguageProvider';

interface TourCardProps {
  tour: Tour;
}

export default function TourCard({ tour }: TourCardProps) {
  const { t } = useTranslation();

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} ${t('common.min')}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <Link href={`/tour/${tour.slug || tour.id}`}>
      <div className="card group cursor-pointer h-full flex flex-col">
        <div className="relative h-48 overflow-hidden">
          {tour.imageUrl ? (
            <Image src={tour.imageUrl} alt={tour.title} fill loading="lazy" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" className="object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 dark:from-gray-700 to-primary-200 dark:to-gray-800 flex items-center justify-center">
              <MapPin className="w-12 h-12 text-primary-400" />
            </div>
          )}
          <div className="absolute top-3 right-3 bg-white dark:bg-gray-800 px-3 py-1 rounded-full shadow-md">
            {tour.price === 0 ? (
              <span className="text-primary font-semibold">{t('common.free')}</span>
            ) : (
              <span className="font-semibold dark:text-gray-100">{tour.price.toFixed(2)}€</span>
            )}
          </div>
        </div>

        <div className="p-4 flex flex-col flex-grow">
          <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-2">
            <MapPin className="w-4 h-4 mr-1" />
            <span>{tour.city}{tour.province ? `, ${tour.province}` : ''}, {tour.country}</span>
          </div>
          <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {tour.title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 mb-4 flex-grow">{tour.description.length > 80 ? tour.description.slice(0, 80).trimEnd() + '...' : tour.description}</p>

          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-3">
            <div className="flex items-center"><Clock className="w-4 h-4 mr-1" />{formatDuration(tour.duration)}</div>
            <div className="flex items-center"><Footprints className="w-4 h-4 mr-1" />{tour.distance.toFixed(1)} km</div>
            <span className="bg-primary-100 text-primary-700 px-2 py-0.5 rounded">{tour.stops} {t('common.stops')}</span>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center">
              {tour.rating > 0 ? (
                <>
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 mr-1" />
                  <span className="font-medium">{tour.rating.toFixed(1)}</span>
                  <span className="text-gray-400 dark:text-gray-500 text-sm ml-1">({tour.reviewCount})</span>
                </>
              ) : (
                <span className="text-gray-400 dark:text-gray-500 text-sm">{t('common.noRatings')}</span>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('common.by')} <span className="text-primary font-medium">{tour.guideName}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
